import React from "react";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";

export type StatTone = "neutral" | "positive" | "warning" | "danger";

const Tile = styled.div<SpaceProps>`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: 16px;
  border: 1px solid ${(p) => p.theme.colors.border ?? p.theme.colors.grey4};
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surface ?? "white"};
  ${space};
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${(p) => p.theme.colors.textMuted ?? p.theme.colors.grey1};
`;

const Value = styled.div`
  margin-top: 4px;
  font-size: 28px;
  line-height: 1.15;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${(p) => p.theme.colors.black};
`;

const Hint = styled.div<{ tone: StatTone }>`
  margin-top: 4px;
  font-size: 13px;
  color: ${(p) =>
    p.tone === "neutral"
      ? (p.theme.colors.textMuted ?? p.theme.colors.grey1)
      : (p.theme.colors[`${p.tone}Text`] ?? p.theme.colors[p.tone])};
  font-weight: ${(p) => (p.tone === "neutral" ? 400 : 600)};
`;

export type StatTileProps = {
  /** What is counted, in sentence case: "Conversations this month". */
  label: React.ReactNode;
  value: React.ReactNode;
  /** A short line under the value: "3 overdue", "+12% on last month". */
  hint?: React.ReactNode;
  /** Colours the hint only; the number stays neutral. */
  tone?: StatTone;
} & SpaceProps;

/** One number with its label. Put several in a grid. */
export function StatTile({
  label,
  value,
  hint,
  tone = "neutral",
  ...rest
}: StatTileProps) {
  return (
    <Tile {...rest}>
      <Label>{label}</Label>
      <Value>{value}</Value>
      {hint != null && <Hint tone={tone}>{hint}</Hint>}
    </Tile>
  );
}
